import React from 'react'
import { Button, Element, Columns } from 'react-bulma-components'
import OptimizedImage from '../optimized-image/optimized-image'
import { Translate } from '../../utils/translations'
import type { RepairConfig } from '../../campaigns'

interface RepairPresentationProps {
  onRequestKit?: () => void
  /** Campaign-specific quantity map (e.g. {red, black} or {sd500, emd500e}). */
  quantity?: Record<string, number>
  /** Repair assets/links for the active campaign. */
  repair?: RepairConfig
}

/**
 * Composant de présentation de la réparation avec participation utilisateur
 */
const RepairPresentation: React.FC<RepairPresentationProps> = ({
  onRequestKit,
  quantity,
  repair,
}) => {
  // The detailed concerned-product block is specific to the B900 red crank.
  const hasRedCrank = quantity?.red !== undefined

  return (
    <Element>
      <Element mb={4}>
        {hasRedCrank && (
          <Element mb={4}>
            <Element renderAs="strong">
              <Translate value="recall.concernedProductQuantity" /> {quantity?.red}
            </Element>
            <Element>
              <Element>
                <Translate value="recall.concernedProductLabel" />
                <Translate value="recall.descriptionRed" />
                <OptimizedImage
                  imageName="red_crank"
                  alt="Panier de basket avec manivelle rouge amovible"
                  priority
                />
              </Element>
            </Element>
          </Element>
        )}
        <Translate value="repair.description" />
      </Element>
      <Columns vCentered>
        {repair?.kitImageName && (
          <Columns.Column size={4} style={{ textAlign: 'center' }}>
            <OptimizedImage
              imageName={repair.kitImageName}
              alt="Repair kit"
              priority
              height={320}
            />
          </Columns.Column>
        )}
        {repair?.videoUrl && (
          <Columns.Column size={4} textAlign="center">
            <Element>
              <iframe
                height="320"
                width="100%"
                src={repair.videoUrl}
                title="Tutorial vidéo de réparation"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </Element>
          </Columns.Column>
        )}
        {repair?.pdfUrl && (
          <Columns.Column size={4} textAlign="center">
            <a
              href={repair.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontWeight: 600,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                textDecoration: 'none',
              }}
            >
              <Element>
                <OptimizedImage
                  imageName="pdf-logo"
                  alt="PDF logo"
                  width={100}
                  height={100}
                  priority
                />
              </Element>
              <Element>
                <Translate value="repair.docLink" />
              </Element>
            </a>
          </Columns.Column>
        )}
      </Columns>
      {onRequestKit && (
        <Element mb={4} style={{ textAlign: 'center' }}>
          <Button
            color="primary"
            onClick={onRequestKit}
            data-testid="request-kit-button"
            style={{
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              maxWidth: '100%',
              minHeight: 'auto',
              padding: '22px 16px',
              fontSize: '14px',
              lineHeight: '1.4',
            }}
          >
            <Translate value="repair.callToAction" />
          </Button>
        </Element>
      )}
      {repair?.showWarranty && (
        <Element mb={3} style={{ textAlign: 'center' }}>
          <Translate value="repair.warranty" />
        </Element>
      )}
    </Element>
  )
}

export default RepairPresentation
